import { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { SaveIcon } from '@app/components/icons';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useCredentialStoresApi, useSecretsApi } from '@app/hooks/useApis';
import { deleteSecretWithAuth } from '@app/utils/secrets';
import type {
  CredentialStore,
  CredentialStoreCreate,
  CredentialStoreUpdate,
  CredentialStoresListResponse,
  Secret,
  SecretCreate
} from '@app/types/sdk';
import { SecretTypeschema } from '@app/types/sdk';

type StoreFormState = {
  name: string;
  ccpHost: string;
  apiPrefix: string;
  appId: string;
  safe: string;
  tlsSkipVerify: boolean;
  tlsCaBundleSecretId: string;
  tlsCaBundlePem: string;
  authMode: 'standard' | 'mtls';
  tlsClientCertSecretId: string;
  tlsClientKeySecretId: string;
  tlsClientKeyPassphraseSecretId: string;
  tlsClientBundlePem: string;
  tlsClientBundlePassphrase: string;
};

type StoreFilters = {
  q: string;
};

const emptyStoreForm = (): StoreFormState => ({
  name: '',
  ccpHost: '',
  apiPrefix: defaultApiPrefix,
  appId: '',
  safe: '',
  tlsSkipVerify: false,
  tlsCaBundleSecretId: '',
  tlsCaBundlePem: '',
  authMode: 'standard',
  tlsClientCertSecretId: '',
  tlsClientKeySecretId: '',
  tlsClientKeyPassphraseSecretId: '',
  tlsClientBundlePem: '',
  tlsClientBundlePassphrase: ''
});

const defaultApiPrefix = '/AIMWebService';

const splitCcpHost = (value?: string): { host: string; apiPrefix: string } => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { host: '', apiPrefix: defaultApiPrefix };
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const host = `${url.protocol}//${url.host}`;
      const apiPrefix =
        url.pathname && url.pathname !== '/' ? url.pathname.replace(/\/$/, '') : defaultApiPrefix;
      return { host, apiPrefix: apiPrefix || defaultApiPrefix };
    } catch {
      return { host: trimmed, apiPrefix: defaultApiPrefix };
    }
  }
  const slashIndex = trimmed.indexOf('/');
  if (slashIndex === -1) {
    return { host: trimmed, apiPrefix: defaultApiPrefix };
  }
  return { host: trimmed.slice(0, slashIndex), apiPrefix: trimmed.slice(slashIndex) };
};

const mapStoreToForm = (store: CredentialStore): StoreFormState => {
  const { host, apiPrefix } = splitCcpHost(store.cyberarkCcpHost ?? '');
  const resolvedPrefix = store.cyberarkApiPrefix?.trim() || apiPrefix || defaultApiPrefix;
  return {
    name: store.name ?? '',
    ccpHost: host,
    apiPrefix: resolvedPrefix,
    appId: store.cyberarkAppId ?? '',
    safe: store.cyberarkSafe ?? '',
    tlsSkipVerify: Boolean(store.cyberarkTlsSkipVerify),
    tlsCaBundleSecretId: store.cyberarkTlsCaBundleSecretId ?? '',
    tlsCaBundlePem: '',
    authMode:
      store.cyberarkTlsClientCertSecretId || store.cyberarkTlsClientKeySecretId
        ? 'mtls'
        : 'standard',
    tlsClientCertSecretId: store.cyberarkTlsClientCertSecretId ?? '',
    tlsClientKeySecretId: store.cyberarkTlsClientKeySecretId ?? '',
    tlsClientKeyPassphraseSecretId: store.cyberarkTlsClientKeyPassphraseSecretId ?? '',
    tlsClientBundlePem: '',
    tlsClientBundlePassphrase: ''
  };
};

const formatTimestamp = (date?: Date | string): string => {
  if (!date) {
    return '—';
  }
  const instance = date instanceof Date ? date : new Date(date);
  return instance.toLocaleString();
};

const CredentialStorePanel = () => {
  const orgId = useActiveOrgId();
  const credentialStoresApi = useCredentialStoresApi();
  const secretsApi = useSecretsApi();

  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [selectedStoreEtag, setSelectedStoreEtag] = useState<string | null>(null);
  const [storeForm, setStoreForm] = useState<StoreFormState>(emptyStoreForm);
  const [loadingStore, setLoadingStore] = useState(false);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<StoreFilters>({ q: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [creatingNew, setCreatingNew] = useState(false);
  const [pendingDeleteStores, setPendingDeleteStores] = useState<CredentialStore[] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [locallyDeletedStoreIds, setLocallyDeletedStoreIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);

  useEffect(() => {
    setLocallyDeletedStoreIds(new Set());
  }, [orgId]);

  type CreatedSecret = { secret: Secret; etag?: string | null };

  const cleanupCreatedSecrets = useCallback(
    async (records: Record<string, CreatedSecret>) => {
      await Promise.all(
        Object.values(records).map(async (entry) => {
          const secretId = entry.secret.id;
          if (!secretId || !orgId) {
            return;
          }
          try {
            await deleteSecretWithAuth({
              secretsApi,
              orgId,
              secretId,
              etag: entry.etag ?? undefined
            });
          } catch (cleanupError) {
            console.warn('Failed to clean up provisional secret', cleanupError);
          }
        })
      );
    },
    [orgId, secretsApi]
  );

  const handleSecretCreation = useCallback(
    async (definitions: Array<{ key: string; create: SecretCreate }>): Promise<Record<string, CreatedSecret>> => {
      if (!orgId || definitions.length === 0) {
        return {};
      }
      const entries: Array<[string, CreatedSecret]> = [];
      try {
        for (const definition of definitions) {
          const response = await secretsApi.orgsOrgIdSecretsPostRaw({
            orgId,
            secretCreate: definition.create
          });
          const secret = await response.value();
          const etag = response.raw.headers.get('ETag');
          if (!secret.id) {
            throw new Error('Secret creation response missing identifier.');
          }
          entries.push([definition.key, { secret, etag }]);
        }
        return Object.fromEntries(entries);
      } catch (error) {
        const createdSoFar = Object.fromEntries(entries);
        if (Object.keys(createdSoFar).length > 0) {
          await cleanupCreatedSecrets(createdSoFar);
        }
        throw error;
      }
    },
    [cleanupCreatedSecrets, orgId, secretsApi]
  );

  const listFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => credentialStoresApi.orgsOrgIdCredentialStoresGet({ orgId, limit: pageSize, after: `offset:${(page - 1) * pageSize}`, q: filters.q.trim() });
  }, [credentialStoresApi, orgId, refreshKey, page, pageSize, filters.q]);

  const {
    data: storeList,
    loading: listLoading,
    error: listError,
    backendMissing
  } = useApiQuery<CredentialStoresListResponse>(listFetcher, [listFetcher]);

  const stores = useMemo(
    () => (storeList?.items ?? []).filter((store) => !store.id || !locallyDeletedStoreIds.has(store.id)),
    [locallyDeletedStoreIds, storeList?.items]
  );

  const reloadList = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const resetSelection = useCallback(() => {
    setSelectedStoreId(null);
    setSelectedStoreEtag(null);
    setStoreForm(emptyStoreForm());
    setSaveError(null);
    setSaveStatus(null);
    setDrawerOpen(false);
    setCreatingNew(false);
  }, []);

  const loadStore = useCallback(
    async (storeId: string) => {
      if (!orgId) {
        return;
      }
      setLoadingStore(true);
      setStoreError(null);
      try {
        const response = await credentialStoresApi.orgsOrgIdCredentialStoresStoreIdGetRaw({ orgId, storeId });
        const store = await response.value();
        setSelectedStoreEtag(response.raw.headers.get('ETag'));
        setStoreForm(mapStoreToForm(store));
      } catch (caught) {
        setStoreError(caught instanceof Error ? caught.message : 'Failed to load credential store.');
        setSelectedStoreEtag(null);
      } finally {
        setLoadingStore(false);
      }
    },
    [credentialStoresApi, orgId]
  );

  useEffect(() => {
    if (!storeList) {
      return;
    }
    if (creatingNew) {
      return;
    }
    if (selectedStoreId && stores.some((store) => store.id === selectedStoreId)) {
      return;
    }
    if (stores.length > 0) {
      setSelectedStoreId(stores[0].id ?? null);
    } else {
      setSelectedStoreId(null);
      setSelectedStoreEtag(null);
      setStoreForm(emptyStoreForm());
    }
  }, [creatingNew, storeList, stores, selectedStoreId]);

  useEffect(() => {
    if (!selectedStoreId) {
      setSelectedStoreEtag(null);
      setStoreForm(emptyStoreForm());
      setSaveError(null);
      setSaveStatus(null);
      return;
    }
    if (drawerOpen) {
      void loadStore(selectedStoreId);
    }
  }, [drawerOpen, loadStore, selectedStoreId]);

  if (!orgId) {
    return <OrgModulePlaceholder intent="Select an organization to manage credential stores." />;
  }

  if (backendMissing) {
    return <OrgModulePlaceholder intent="Credential store endpoints are unavailable." />;
  }

  const isEditing = Boolean(selectedStoreId);
  const hasStores = stores.length > 0;

  const filteredStores = stores;
  const storeTotal = storeList?.meta?.exactTotal ?? stores.length;
  const pageCount = Math.max(1, Math.ceil(storeTotal / pageSize));
  const pagedStores = filteredStores;

  useEffect(() => {
    setPage((prev) => Math.min(prev, pageCount));
  }, [pageCount]);

  const storeColumns = useMemo<Array<ListWorkspaceColumn<CredentialStore>>>(() => {
    return [
      {
        key: 'name',
        header: 'Name',
        render: (row) => row.name ?? row.id ?? '—'
      },
      {
        key: 'ccp',
        header: 'CCP Host',
        render: (row) => row.cyberarkCcpHost ?? '—'
      },
      {
        key: 'appId',
        header: 'Application ID',
        render: (row) => row.cyberarkAppId ?? '—'
      },
      {
        key: 'safe',
        header: 'Safe',
        render: (row) => row.cyberarkSafe ?? '—'
      },
      {
        key: 'tls',
        header: 'TLS',
        render: (row) => {
          if (row.cyberarkTlsClientCertSecretId || row.cyberarkTlsClientKeySecretId) {
            return row.cyberarkTlsSkipVerify ? 'mTLS (skip verify)' : 'mTLS';
          }
          return row.cyberarkTlsSkipVerify ? 'Skip verify' : 'Verify';
        }
      },
      {
        key: 'updated',
        header: 'Updated',
        render: (row) => formatTimestamp(row.updatedAt)
      }
    ];
  }, []);

  const handleSubmit = async () => {
    if (!orgId) {
      return;
    }
    const name = storeForm.name.trim();
    const rawCcpHost = storeForm.ccpHost.trim();
    const appId = storeForm.appId.trim();
    const safe = storeForm.safe.trim();
    const caBundleId = storeForm.tlsCaBundleSecretId.trim();
    const caBundlePem = storeForm.tlsCaBundlePem.trim();
    const clientCertId = storeForm.tlsClientCertSecretId.trim();
    const clientKeyId = storeForm.tlsClientKeySecretId.trim();
    const clientKeyPassphraseId = storeForm.tlsClientKeyPassphraseSecretId.trim();
    const clientBundlePem = storeForm.tlsClientBundlePem.trim();
    const clientBundlePassphrase = storeForm.tlsClientBundlePassphrase.trim();

    if (!name || !rawCcpHost || !appId || !safe) {
      setSaveError('Name, CCP host, Application ID, and Safe are required.');
      return;
    }

    if (
      storeForm.authMode === 'mtls' &&
      !clientBundlePem &&
      (!clientCertId || !clientKeyId)
    ) {
      setSaveError('Client certificate bundle (PEM) is required for mTLS.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveStatus(null);

    const { host: parsedHost, apiPrefix: parsedPrefix } = splitCcpHost(rawCcpHost);
    const trimmedPrefix = storeForm.apiPrefix.trim();
    const finalApiPrefix =
      (trimmedPrefix === '' || trimmedPrefix === defaultApiPrefix) && parsedPrefix
        ? parsedPrefix
        : trimmedPrefix || defaultApiPrefix;
    const finalCcpHost = parsedHost || rawCcpHost;
    let resolvedCaBundleId = caBundleId;
    let resolvedClientCertId = clientCertId;
    let resolvedClientKeyId = clientKeyId;
    let resolvedClientKeyPassphraseId = clientKeyPassphraseId;
    let createdSecrets: Record<string, CreatedSecret> = {};

    try {
      const definitions: Array<{ key: string; create: SecretCreate }> = [];
      if (caBundlePem) {
        definitions.push({
          key: 'cyberarkCaBundle',
          create: { type: SecretTypeschema.TlsCaBundlePem, value: caBundlePem }
        });
      }
      if (storeForm.authMode === 'mtls' && clientBundlePem) {
        definitions.push(
          {
            key: 'cyberarkClientCert',
            create: { type: SecretTypeschema.TlsClientCertPem, value: clientBundlePem }
          },
          {
            key: 'cyberarkClientKey',
            create: { type: SecretTypeschema.TlsClientKeyPem, value: clientBundlePem }
          }
        );
        if (clientBundlePassphrase) {
          definitions.push({
            key: 'cyberarkClientKeyPassphrase',
            create: { type: SecretTypeschema.Password, value: clientBundlePassphrase }
          });
        }
      }
      if (definitions.length > 0) {
        createdSecrets = await handleSecretCreation(definitions);
        resolvedCaBundleId = createdSecrets['cyberarkCaBundle']?.secret.id ?? resolvedCaBundleId;
        resolvedClientCertId = createdSecrets['cyberarkClientCert']?.secret.id ?? resolvedClientCertId;
        resolvedClientKeyId = createdSecrets['cyberarkClientKey']?.secret.id ?? resolvedClientKeyId;
        resolvedClientKeyPassphraseId =
          createdSecrets['cyberarkClientKeyPassphrase']?.secret.id ?? resolvedClientKeyPassphraseId;
      }

      if (isEditing && selectedStoreId) {
        const payload: CredentialStoreUpdate = {
          name,
          cyberarkCcpHost: finalCcpHost,
          cyberarkApiPrefix: finalApiPrefix,
          cyberarkAppId: appId,
          cyberarkSafe: safe,
          cyberarkTlsSkipVerify: storeForm.tlsSkipVerify,
          cyberarkTlsCaBundleSecretId: resolvedCaBundleId || undefined,
          cyberarkTlsClientCertSecretId:
            storeForm.authMode === 'mtls' ? resolvedClientCertId || undefined : undefined,
          cyberarkTlsClientKeySecretId:
            storeForm.authMode === 'mtls' ? resolvedClientKeyId || undefined : undefined,
          cyberarkTlsClientKeyPassphraseSecretId:
            storeForm.authMode === 'mtls' ? resolvedClientKeyPassphraseId || undefined : undefined
        };
        const response = await credentialStoresApi.orgsOrgIdCredentialStoresStoreIdPatchRaw({
          orgId,
          storeId: selectedStoreId,
          credentialStoreUpdate: payload,
          ifMatch: selectedStoreEtag ?? undefined
        });
        await response.value();
        setSaveStatus('Credential store updated.');
        resetSelection();
      } else {
        const payload: CredentialStoreCreate = {
          name,
          type: 'cyberark',
          cyberarkCcpHost: finalCcpHost,
          cyberarkApiPrefix: finalApiPrefix,
          cyberarkAppId: appId,
          cyberarkSafe: safe,
          cyberarkTlsSkipVerify: storeForm.tlsSkipVerify,
          cyberarkTlsCaBundleSecretId: resolvedCaBundleId || undefined,
          cyberarkTlsClientCertSecretId:
            storeForm.authMode === 'mtls' ? resolvedClientCertId || undefined : undefined,
          cyberarkTlsClientKeySecretId:
            storeForm.authMode === 'mtls' ? resolvedClientKeyId || undefined : undefined,
          cyberarkTlsClientKeyPassphraseSecretId:
            storeForm.authMode === 'mtls' ? resolvedClientKeyPassphraseId || undefined : undefined
        };
        await credentialStoresApi.orgsOrgIdCredentialStoresPost({ orgId, credentialStoreCreate: payload });
        setSaveStatus('Credential store created.');
        resetSelection();
      }
      reloadList();
    } catch (caught) {
      if (Object.keys(createdSecrets).length > 0) {
        await cleanupCreatedSecrets(createdSecrets);
      }
      setSaveError(caught instanceof Error ? caught.message : 'Failed to save credential store.');
    } finally {
      setSaving(false);
    }
  };

  const filterActive = filters.q.trim().length > 0;
  const filterBar = (
    <div className="list-workspace__filter-grid">
      <label className="form-field">
        <span className="form-field__label">Search</span>
        <input
          className="input"
          placeholder="Search by name, host, application ID, or safe"
          value={filters.q}
          onChange={(event) => setFilters({ q: event.target.value })}
        />
      </label>
    </div>
  );

  const handleRowClick = (store: CredentialStore) => {
    setSelectedStoreId(store.id ?? null);
    setSaveError(null);
    setSaveStatus(null);
    setCreatingNew(false);
    setDrawerOpen(true);
  };

  const handleDeleteSelected = (rows: CredentialStore[]) => {
    if (!orgId || rows.length === 0 || deleteBusy) {
      return;
    }
    setPendingDeleteStores(rows);
  };

  const confirmDeleteSelected = async () => {
    const rows = pendingDeleteStores;
    if (!orgId || !rows || rows.length === 0 || deleteBusy) {
      return;
    }
    setDeleteBusy(true);
    const deletedIds: string[] = [];
    let failedCount = 0;
    for (const row of rows) {
      if (!row.id) {
        failedCount += 1;
        continue;
      }
      try {
        const response = await credentialStoresApi.orgsOrgIdCredentialStoresStoreIdGetRaw({
          orgId,
          storeId: row.id
        });
        const etag = response.raw.headers.get('ETag');
        await credentialStoresApi.orgsOrgIdCredentialStoresStoreIdDelete({
          orgId,
          storeId: row.id,
          ifMatch: etag ?? undefined
        });
        deletedIds.push(row.id);
      } catch (error) {
        failedCount += 1;
        console.error('Failed to delete credential store.', error);
      }
    }
    if (deletedIds.length > 0) {
      setLocallyDeletedStoreIds((current) => new Set([...current, ...deletedIds]));
      setPendingDeleteStores(null);
      if (selectedStoreId && deletedIds.includes(selectedStoreId)) {
        setDrawerOpen(false);
        resetSelection();
      }
      setDeleteToast({
        tone: failedCount > 0 ? 'warning' : 'success',
        message:
          failedCount > 0
            ? `${deletedIds.length} of ${rows.length} credential stores deleted. ${failedCount} could not be deleted.`
            : `${deletedIds.length} credential store${deletedIds.length === 1 ? '' : 's'} deleted successfully.`
      });
      reloadList();
    } else {
      setDeleteToast({ tone: 'error', message: 'Failed to delete the selected credential stores.' });
    }
    setDeleteBusy(false);
  };

  const drawerActions = (
    <div className="drawer-icon-actions">
      <button
        type="button"
        className="drawer-icon-button"
        aria-label="Save CyberArk CCP store"
        title="Save CyberArk CCP store"
        onClick={() => void handleSubmit()}
        disabled={saving}
      >
        <SaveIcon size={18} />
      </button>
    </div>
  );

  return (
    <>
      <ListWorkspace
        headerLabel="CyberArk CCP"
        rows={pagedStores}
        columns={storeColumns}
        rowId={(row, index) => row.id ?? index}
        highlightRowId={selectedStoreId}
        onRowClick={handleRowClick}
        onAdd={() => {
          setSelectedStoreId(null);
          setSelectedStoreEtag(null);
          setStoreForm(emptyStoreForm());
          setSaveError(null);
          setSaveStatus(null);
          setCreatingNew(true);
          setDrawerOpen(true);
        }}
        onDeleteSelected={handleDeleteSelected}
        onRefresh={reloadList}
        loading={listLoading}
        headerStatus={
          listError ? <div className="list-workspace__status">Failed to load CyberArk CCP stores: {listError.message}</div> : undefined
        }
        filterBar={filterBar}
        filterActive={filterActive}
        onFilterReset={() => setFilters({ q: '' })}
        page={page}
        pageSize={pageSize}
        totalCount={storeTotal}
        onPageChange={(next) => setPage(Math.min(Math.max(1, next), pageCount))}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        selectionDisabled={!hasStores}
        emptyState={listLoading ? <p>Loading CyberArk CCP stores…</p> : <p>No CyberArk CCP stores configured yet.</p>}
      />

      <Drawer
        open={drawerOpen}
        title={isEditing ? 'Edit CyberArk CCP store' : 'New CyberArk CCP store'}
        subtitle="CyberArk CCP configuration"
        onClose={() => {
          setDrawerOpen(false);
          setCreatingNew(false);
        }}
        actions={drawerActions}
      >
        <section className="section-card">
          <h3 className="section-card__title">Connection details</h3>
          <p className="helper-text">Manage external credential stores (CyberArk CCP) and select them when creating credentials.</p>
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span className="form-field__label">Store name</span>
              <input
                className="input"
                value={storeForm.name}
                onChange={(event) => setStoreForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Example: Primary CyberArk CCP"
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">CCP URL</span>
              <input
                className="input"
                value={storeForm.ccpHost}
                onChange={(event) => setStoreForm((prev) => ({ ...prev, ccpHost: event.target.value }))}
                placeholder="https://ccp.example.com:port"
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">Application ID</span>
              <input
                className="input"
                value={storeForm.appId}
                onChange={(event) => setStoreForm((prev) => ({ ...prev, appId: event.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">API Prefix</span>
              <input
                className="input"
                value={storeForm.apiPrefix}
                onChange={(event) => setStoreForm((prev) => ({ ...prev, apiPrefix: event.target.value }))}
                placeholder="/AIMWebService"
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">Safe</span>
              <input
                className="input"
                value={storeForm.safe}
                onChange={(event) => setStoreForm((prev) => ({ ...prev, safe: event.target.value }))}
              />
            </label>
          </div>
          <label className="checkbox-row">
            <input
              className="checkbox"
              type="checkbox"
              checked={storeForm.tlsSkipVerify}
              onChange={(event) => setStoreForm((prev) => ({ ...prev, tlsSkipVerify: event.target.checked }))}
            />
            Skip TLS verification (not recommended)
          </label>
          {!storeForm.tlsSkipVerify && (
            <>
              <label className="form-field">
                <span className="form-field__label">CA bundle (PEM, optional)</span>
                <textarea
                  className="textarea"
                  value={storeForm.tlsCaBundlePem}
                  onChange={(event) => setStoreForm((prev) => ({ ...prev, tlsCaBundlePem: event.target.value }))}
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
                <span className="form-field__hint">
                  Use this when CCP uses a private CA. Stored securely server-side.
                </span>
              </label>
              {storeForm.tlsCaBundlePem.trim() === '' && storeForm.tlsCaBundleSecretId && (
                <p className="form-field__hint">Using existing CA bundle secret.</p>
              )}
            </>
          )}
          <label className="form-field">
            <span className="form-field__label">Authentication</span>
            <select
              className="input"
              value={storeForm.authMode}
              onChange={(event) =>
                setStoreForm((prev) => {
                  const nextMode = event.target.value === 'mtls' ? 'mtls' : 'standard';
                  if (nextMode === 'standard') {
                    return {
                      ...prev,
                      authMode: nextMode,
                      tlsClientCertSecretId: '',
                      tlsClientKeySecretId: '',
                      tlsClientKeyPassphraseSecretId: '',
                      tlsClientBundlePem: '',
                      tlsClientBundlePassphrase: ''
                    };
                  }
                  return { ...prev, authMode: nextMode };
                })
              }
            >
              <option value="standard">TLS (AppID only)</option>
              <option value="mtls">mTLS (client certificate)</option>
            </select>
          </label>
          {storeForm.authMode === 'mtls' && (
            <>
              <label className="form-field">
                <span className="form-field__label">Client certificate bundle (PEM)</span>
                <textarea
                  className="textarea"
                  value={storeForm.tlsClientBundlePem}
                  onChange={(event) => setStoreForm((prev) => ({ ...prev, tlsClientBundlePem: event.target.value }))}
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
                <span className="form-field__hint">
                  Paste a PEM bundle containing the client certificate and private key. Stored securely server-side.
                </span>
              </label>
              <label className="form-field">
                <span className="form-field__label">Passphrase (optional)</span>
                <input
                  className="input"
                  type="password"
                  value={storeForm.tlsClientBundlePassphrase}
                  onChange={(event) => setStoreForm((prev) => ({ ...prev, tlsClientBundlePassphrase: event.target.value }))}
                />
              </label>
              {storeForm.tlsClientBundlePem.trim() === '' &&
                (storeForm.tlsClientCertSecretId || storeForm.tlsClientKeySecretId) && (
                  <p className="form-field__hint">Using existing client certificate and key secrets.</p>
                )}
            </>
          )}
          {loadingStore && <p>Loading store details…</p>}
          {storeError && <p className="form-error">{storeError}</p>}
          {saveError && <p className="form-error">{saveError}</p>}
          {saveStatus && <p className="helper-text">{saveStatus}</p>}
        </section>
      </Drawer>
      <DeleteConfirmationModal
        open={pendingDeleteStores != null}
        title={pendingDeleteStores?.length === 1 ? 'Delete credential store?' : `Delete ${pendingDeleteStores?.length ?? 0} credential stores?`}
        description="This permanently removes the selected CyberArk CCP store configuration."
        deleting={deleteBusy}
        onCancel={() => setPendingDeleteStores(null)}
        onConfirm={() => void confirmDeleteSelected()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </>
  );
};

export default CredentialStorePanel;
